/**
 * Protected Route Component
 *
 * Wraps dashboard routes to ensure user is authenticated
 * Shows loading state while checking authentication
 * Redirects to login if not authenticated
 *
 * Usage:
 * ```tsx
 * <ProtectedRoute>
 *   <DashboardContent />
 * </ProtectedRoute>
 * ```
 */

import { ReactNode, useEffect } from 'react'
import { Navigate } from 'react-router-dom'
import { useRequireAuth } from '@/lib/auth-context'
import type { FeatureFlags } from '@/lib/api/auth'
import { hasPermission, PERMISSION_DENIED_HINT, type PermissionCode } from '@/lib/permissions'
import { showErrorToast } from '@/lib/toast'

interface ProtectedRouteProps {
  children: ReactNode
  /**
   * When set, the route also requires the given backend feature flag to be enabled
   * (surfaced via /auth/me). If the flag is off, the user is redirected to /home —
   * so a hidden entry point can't be reached by typing the URL directly.
   */
  feature?: keyof FeatureFlags
  /**
   * When set, the route also requires the permission. Missing it redirects to /home rather than
   * rendering a page that would 403 on every call — a menu entry is hidden for the same user, so
   * reaching this is either a typed URL or a stale bookmark.
   *
   * SOLY-REGIE-006 — this stays ONE code, deliberately. « Whoever can neither read nor write does
   * not come in » is enforced where it can be true everywhere: PermissionService makes
   * `planning:manage` imply `planning:read`, so any session allowed into the cockpit carries
   * `planning:read`. Reading both codes HERE would have been worse than the bug it looked like it
   * fixed: the ~70 read endpoints still demand `read` alone, so a manage-only role would have been
   * let through the door onto a cockpit answering 403 to everything — the empty planning this very
   * branch exists to abolish.
   */
  permission?: PermissionCode
  /**
   * SOLY-SUPPLIER-002 — when set, the organization must have this module (SaaS entitlement switched
   * on by Soly, `user.modules`). Off: redirect to /home, like a feature flag.
   */
  module?: string
}

export function ProtectedRoute({ children, feature, permission, module }: ProtectedRouteProps) {
  const { user, isLoading } = useRequireAuth()

  const featureMissing =
    user != null && ((feature != null && !user.features?.[feature]) || (module != null && !(user.modules ?? []).includes(module)))
  const permissionMissing = user != null && permission != null && !hasPermission(user, permission)

  // A silent redirect reads as a bug — the user lands on the home page with no idea why, and calls
  // the patron to say « Soly ne marche plus ». The sentences have existed in permissions.ts since
  // SOLY-PERM-001 and were never shown anywhere. It lives in an effect because firing a toast
  // during render is a side effect; under StrictMode that costs a duplicate toast in dev, which is
  // the cheaper of the two problems.
  useEffect(() => {
    if (permissionMissing && permission != null) {
      showErrorToast(PERMISSION_DENIED_HINT[permission])
    }
  }, [permissionMissing, permission])

  // Show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent mb-4"></div>
          <p className="text-muted-foreground">Chargement...</p>
        </div>
      </div>
    )
  }

  // If not authenticated, useRequireAuth will redirect
  // So we only render children if user exists
  if (!user) {
    return null
  }

  // Feature-flagged route: redirect away when the flag is off.
  if (featureMissing) {
    return <Navigate to="/home" replace />
  }

  // Permission-gated route (SOLY-PERM-001).
  if (permissionMissing) {
    return <Navigate to="/home" replace />
  }

  return <>{children}</>
}
